import { combineReducers, compose, createSelector } from '@reduxjs/toolkit';

import wallPreviewsAdapter from './wall_previews.adapter';
import slice from './wall_previews.slice';

import { selectCartPhotos } from 'selectors/cart_photos';
import { selectDefaultPPI } from 'selectors/ppi';

import canvases from 'domains/canvases';
import products from 'domains/products';
import dimensions from 'domains/stage_dimensions';

import { notNull, validCustomSize } from 'utils/type_guards';

import type { RootState } from 'store/wos_store';
import type { Point } from 'types/common_types';

/** Represents the slice as it exists in the final store's reducer */
const combined = combineReducers({ [slice.name]: slice.reducer });
type GlobalState = ReturnType<typeof combined>;

/** Get the local state for the current domain */
const localState = (state: GlobalState) => state[slice.name];
type LocalState = ReturnType<typeof localState>;

const selectWallPreviewStatus = (state: LocalState) => state.status;
const selectLatestWallPreview = (state: LocalState) => {
  const latestId = state.ids[state.ids.length - 1];
  return state.entities[latestId];
};

/**
 * For saved wall preview rendering, we currently need to send the pixel
 * position of a canvas within the wall preview, accounting for the possibility
 * of margins between the stage area and the image background.
 *
 * We are returning these in an object keyed by the canvas ID so when mapping
 * the canvases array when saving a wall preview, we can just pluck the right one.
 */
const selectAdjustedCanvasPositionsForWallPreview = (state: RootState) => {
  const screenDimensions = dimensions.selectors.screenDimensions(state);
  const wallPreviewDimensions = dimensions.selectors.wallPreviewDimensions(state);
  const allCanvases = canvases.selectors.selectAll(state);
  const arrangementPosition = canvases.selectors.arrangementPosition(state);
  const ppi = selectDefaultPPI(state);

  if (!wallPreviewDimensions) return {};

  const xMarginAdjust = screenDimensions.width / 2 - wallPreviewDimensions.width / 2;
  const yMarginAdjust = screenDimensions.height / 2 - wallPreviewDimensions.height / 2;

  const adjustedPositions: Record<string, Point> = {};

  allCanvases.forEach((canvas) => {
    adjustedPositions[canvas.id] = {
      x: canvas.position.x * ppi + arrangementPosition.x * ppi - xMarginAdjust,
      y: canvas.position.y * ppi + arrangementPosition.y * ppi - yMarginAdjust,
    };
  });

  return adjustedPositions;
};

const selectCanvasesForSaveWallPreview = createSelector(
  [
    canvases.selectors.selectAll,
    selectAdjustedCanvasPositionsForWallPreview,
    products.selectors.selectEntities,
    selectCartPhotos,
    selectDefaultPPI,
  ],
  (allCanvases, canvasPositionsById, productsById, cartPhotos, ppi) => {
    return allCanvases
      .map((canvas) => {
        if (!canvas.product || !canvas.options) return null;

        const canvasProduct = productsById[canvas.product];
        if (!canvasProduct) return null;

        const productSize = products.utils.calculateProductSize(canvasProduct, canvas);
        const customSize =
          products.utils.isCustomSizeProduct(canvasProduct) && validCustomSize(canvas)
            ? { height: canvas.height, width: canvas.width }
            : null;

        const canvasId = canvas.id;
        const canvasPosition = canvasPositionsById[canvasId];
        if (!canvasPosition) return null;

        const photos = cartPhotos
          .filter((photo) => photo.canvasId === canvasId)
          .map((photo) => photo.cartCrop);

        return {
          crop_data: {
            canvasDegreesRotated: canvas.rotation,
            borderColor: canvas.borderColor,
            photos,
          },
          option_ids: Object.values(canvas.options).filter(notNull),
          custom_size: customSize,
          product_id: canvasProduct.id,
          template_id: canvas.product,
          product_dimensions: { width: productSize.width * ppi, height: productSize.height * ppi },
          position: canvasPosition,
        };
      })
      .filter(notNull);
  }
);

export default {
  ...wallPreviewsAdapter.getSelectors(localState),
  status: compose(selectWallPreviewStatus, localState),
  latest: compose(selectLatestWallPreview, localState),
  forSave: selectCanvasesForSaveWallPreview,
};
